import React, { useState, useEffect, useRef } from 'react';
import { useSandbox } from '../../contexts/SandboxContext';

const MathTable: React.FC<{ title: string; data: any }> = ({ title, data }) => {
  if (!data || !data.itemLog || data.itemLog.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-white mb-2 uppercase border-b border-gray-700 pb-1">{title}</h3>
      <table className="w-full text-xs text-left mb-2">
        <thead className="text-gray-400">
          <tr>
            <th className="py-1 font-normal">Item</th>
            <th className="py-1 text-right font-normal">Qty</th>
            <th className="py-1 text-right font-normal">Dead Wt</th>
            <th className="py-1 text-right font-normal">Base Vol</th>
            <th className="py-1 text-right font-normal">Eff</th>
            <th className="py-1 text-right font-normal">Pack Vol</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {data.itemLog.map((log: any, idx: number) => {
            const isOverridden = log.isOverridden;
            const rowClass = isOverridden ? "text-gray-500 line-through italic" : "text-green-300";
            return (
              <tr key={idx} className={rowClass}>
                <td className="py-1 pr-2 truncate max-w-[120px]" title={log.name}>{log.name}</td>
                <td className="py-1 text-right">{log.quantity}</td>
                <td className="py-1 text-right">{log.deadWeightKg?.toFixed(2)}</td>
                <td className="py-1 text-right">{log.baseVolumetricKg?.toFixed(2)}</td>
                <td className="py-1 text-right">{log.packingEfficiency}%</td>
                <td className="py-1 text-right">{log.packedVolumetricKg?.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="bg-gray-800 p-2 rounded text-xs flex justify-between items-center">
        <span className="text-gray-400">Max({data.totalDeadWeight?.toFixed(2)}kg, {data.totalVolumetricWeight?.toFixed(2)}kg)</span>
        <span className="font-bold text-white">= {data.chargeableWeight?.toFixed(2)}kg</span>
      </div>
    </div>
  );
};

export const DebugDrawer: React.FC = () => {
  const { isSandboxMode, telemetry } = useSandbox();

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      const dx = e.clientX - dragStartPos.current.x;
      const dy = e.clientY - dragStartPos.current.y;
      
      setPosition(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));
      
      dragStartPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  if (!isSandboxMode) {
    return null;
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  };

  return (
    <div 
      className="fixed right-4 top-20 h-fit max-h-[85vh] w-[450px] bg-gray-900 text-green-400 shadow-2xl z-50 flex flex-col font-mono text-sm border border-gray-700 rounded-lg overflow-hidden"
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
    >
      <div 
        className="bg-gray-800 p-3 cursor-move flex justify-between items-center select-none"
        onMouseDown={handleMouseDown}
      >
        <h2 className="text-sm font-bold text-white">⚙️ Sandbox Telemetry</h2>
      </div>
      
      <div className="p-4 overflow-y-auto">
        <p className="text-xs text-gray-500 italic mb-2">
          Note: This panel strictly debugs Pass 3 dimensional physics and box packing. It does not display Base Packs or Item Handling surcharges.
        </p>
        {telemetry?.collection && <MathTable title="Return Volumetric Calcs" data={telemetry.collection} />}
        {telemetry?.deployment && <MathTable title="Deploy Volumetric Calcs" data={telemetry.deployment} />}
        
        {(!telemetry?.collection?.itemLog?.length && !telemetry?.deployment?.itemLog?.length) && (
          <p className="text-gray-500 italic text-xs">Awaiting telemetry data...</p>
        )}
      </div>
    </div>
  );
};
